// src/store/stickerCartSlice.js
//
// КОРЗИНА СТИКЕРОВ В СОСТОЯНИИ ПРИЛОЖЕНИЯ (п.87, сессия 2026-10-29).
//
// 🔑 ПОЧЕМУ ОТДЕЛЬНЫЙ СРЕЗ, А НЕ В `cartSlice`.
//    `cartSlice` описывает ТОВАРНУЮ корзину (`/cart`, `cart_items` со
//    ссылкой на `products`). У стикера нет товара: есть карточка каталога
//    или картинка клиента, размер и квота. Смешать их в одном массиве
//    `items` значило бы, что КАЖДОЕ место, читающее `state.cart.items`
//    (корзина, оформление, шапка со счётчиком, письма), обязано
//    проверять «а это товар или стикер?». Забытая проверка — не 500,
//    а неверная сумма или пустая позиция. Тот же класс, что §7 №1.
//
// ⚠️ СЧЁТЧИК В ШАПКЕ. Пока суммируем ДВА среза в компоненте-счётчике,
//    а не переносим их в один. Это видно и проверяемо, в отличие от
//    «одного массива с двумя формами элементов».
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { stickerCartAPI } from '../api/stickerCart.js';

const pickError = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

export const getStickerCart = createAsyncThunk(
  'stickerCart/get',
  async (_, { rejectWithValue }) => {
    try {
      const r = await stickerCartAPI.get();
      return r.data.data;
    } catch (error) {
      return rejectWithValue(pickError(error, 'Не удалось загрузить корзину'));
    }
  },
);

export const addStickerToCart = createAsyncThunk(
  'stickerCart/add',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const r = await stickerCartAPI.add(data);
      // ⚠️ После изменения ВСЕГДА перечитываем корзину, а не дописываем
      //    локально: сервер мог объединить строки (та же карточка и тот же
      //    размер) и пересчитать сумму. Локальная склейка разошлась бы
      //    с сервером — и клиент увидел бы другую сумму на оформлении.
      dispatch(getStickerCart());
      return r.data.data;
    } catch (error) {
      return rejectWithValue(pickError(error, 'Не удалось добавить в корзину'));
    }
  },
);

export const updateStickerCartItem = createAsyncThunk(
  'stickerCart/update',
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const r = await stickerCartAPI.update(id, data);
      dispatch(getStickerCart());
      return r.data.data;
    } catch (error) {
      return rejectWithValue(pickError(error, 'Не удалось изменить количество'));
    }
  },
);

export const removeStickerCartItem = createAsyncThunk(
  'stickerCart/remove',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await stickerCartAPI.remove(id);
      dispatch(getStickerCart());
      return id;
    } catch (error) {
      return rejectWithValue(pickError(error, 'Не удалось удалить позицию'));
    }
  },
);

export const uploadStickerCartImage = createAsyncThunk(
  'stickerCart/uploadImage',
  async ({ id, file, onProgress }, { rejectWithValue, dispatch }) => {
    try {
      const r = await stickerCartAPI.uploadImage(id, file, onProgress);
      dispatch(getStickerCart());
      return r.data.data;
    } catch (error) {
      return rejectWithValue(pickError(error, 'Не удалось загрузить картинку'));
    }
  },
);

const initialState = {
  items: [],
  total: 0,
  count: 0,      // ШТУК стикеров, а не строк
  lines: 0,      // строк корзины
  isLoading: false,
  // 🔑 Отличает «ещё не загружали» от «загрузили, пусто». Без этого
  //    оформление заказа решает «корзина пуста» раньше, чем она пришла
  //    (ровно этот дефект чинили у товарной корзины, сессия 7).
  isLoaded: false,
  error: null,
};

const stickerCartSlice = createSlice({
  name: 'stickerCart',
  initialState,
  reducers: {
    clearStickerCartError: (state) => { state.error = null; },
    resetStickerCart: (state) => {
      state.items = [];
      state.total = 0;
      state.count = 0;
      state.lines = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStickerCart.pending, (state) => { state.isLoading = true; })
      .addCase(getStickerCart.fulfilled, (state, action) => {
        const d = action.payload || {};
        state.isLoading = false;
        state.isLoaded = true;
        state.items = d.items || [];
        state.total = d.total || 0;
        state.count = d.count || 0;
        state.lines = d.lines ?? (d.items || []).length;
        state.error = null;
      })
      .addCase(getStickerCart.rejected, (state, action) => {
        state.isLoading = false;
        // Даже при ошибке снимаем «неопределённость»: иначе UI навсегда
        // застынет в «не знаю» и не покажет ни корзину, ни ошибку.
        state.isLoaded = true;
        state.error = action.payload;
      })
      .addCase(addStickerToCart.pending, (state) => { state.isLoading = true; })
      .addCase(addStickerToCart.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(addStickerToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearStickerCartError, resetStickerCart } = stickerCartSlice.actions;
export default stickerCartSlice.reducer;
